import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { FaDownload, FaExclamationTriangle } from 'react-icons/fa';
import { AuthContext } from '../../../components/AuthContext';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { useNavigate } from 'react-router-dom';

export default function SchemesReports() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('dealer');
  const [dealerData, setDealerData] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState('this_month');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedDealerId, setSelectedDealerId] = useState(null);
  const [dealerDetails, setDealerDetails] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showDeprecationBanner, setShowDeprecationBanner] = useState(true);

  // Create API instance
  const api = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
    withCredentials: true,
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json',
    }
  });

  // Fetch available campaigns
  const fetchCampaigns = async () => {
    try {
      const response = await api.get('/campaigns');
      setCampaigns(response.data);

      // Set first active campaign as default if available
      const activeCampaign = response.data.find(camp =>
        camp.is_active &&
        new Date(camp.date_from) <= new Date() &&
        new Date(camp.date_to) >= new Date()
      );

      if (activeCampaign) {
        setSelectedCampaign(activeCampaign._id);
      } else if (response.data.length > 0) {
        setSelectedCampaign(response.data[0]._id);
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  };

  // Fetch summary statistics
  const fetchSummary = async () => {
    setLoading(true);
    try {
      const response = await api.get('/reports/dealer-points/summary', {
        params: {
          period,
          campaignId: selectedCampaign
        }
      });
      setSummary(response.data.data || {});
    } catch (error) {
      console.error('Error fetching summary:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch dealer performance data
  const fetchDealerData = async (page = 1) => {
    setLoading(true);
    try {
      const response = await api.get('/reports/dealer-points/dealer-performance', {
        params: {
          period,
          page,
          limit: 10,
          campaignId: selectedCampaign
        }
      });

      setDealerData(response.data.data || []);
      setTotalPages(response.data.pagination?.pages || 1);

      if (!selectedCampaign && response.data.campaign) {
        setSelectedCampaign(response.data.campaign._id);
      }
    } catch (error) {
      console.error('Error fetching dealer data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch dealer details
  const fetchDealerDetails = async (dealerId) => {
    setLoadingDetails(true);
    try {
      const response = await api.get(`/reports/dealer-points/dealer-details/${dealerId}`, {
        params: {
          period,
          campaignId: selectedCampaign
        }
      });

      setDealerDetails(response.data);
    } catch (error) {
      console.error('Error fetching dealer details:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Handle campaign change
  const handleCampaignChange = (e) => {
    setSelectedCampaign(e.target.value);
    setCurrentPage(1);
    setSelectedDealerId(null);
    setDealerDetails(null);
  };

  // Handle period change
  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
    setCurrentPage(1);
    setSelectedDealerId(null);
    setDealerDetails(null);
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    setSelectedDealerId(null);
    setDealerDetails(null);
  };

  // Handle dealer row click
  const handleDealerClick = (dealerId) => {
    setSelectedDealerId(dealerId);
    fetchDealerDetails(dealerId);
  };

  // Export current data
  const handleExport = () => {
    // Convert dealer data to CSV
    let csvContent = 'data:text/csv;charset=utf-8,';

    // Add headers
    csvContent += 'Dealer Name,Total Sales,Total Points,Voucher Count\n';

    // Add data rows
    dealerData.forEach(dealer => {
      csvContent += `${dealer.dealerName},${dealer.totalSales},${dealer.totalPoints},${dealer.voucherCount}\n`;
    });

    // Create download link
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `dealer_performance_${period}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Export dealer details
  const handleExportDetails = () => {
    if (!dealerDetails) return;

    // Convert dealer details to CSV
    let csvContent = 'data:text/csv;charset=utf-8,';

    // Add headers
    csvContent += 'Voucher Number,Date,Total Amount,Total Points\n';

    // Add voucher data
    dealerDetails.vouchers.forEach(voucher => {
      csvContent += `${voucher.voucherNumber},${format(new Date(voucher.date), 'yyyy-MM-dd')},${voucher.totalAmount},${voucher.totalPoints}\n`;
    });

    // Create download link
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `dealer_detail_${dealerDetails.dealer.name}_${period}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Generate pagination buttons
  const renderPaginationButtons = () => {
    const buttons = [];

    // Previous button
    buttons.push(
      <button
        key="prev"
        onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
        className={`px-3 py-1 rounded ${currentPage === 1 ? 'bg-gray-200 text-gray-500' : 'bg-gray-300 hover:bg-gray-400 text-gray-700'}`}
      >
        &laquo; Prev
      </button>
    );

    // Page number buttons
    const maxButtons = 5;
    const startPage = Math.max(1, Math.min(currentPage - Math.floor(maxButtons / 2), totalPages - maxButtons + 1));

    for (let i = startPage; i < startPage + maxButtons && i <= totalPages; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1 rounded mx-1 ${currentPage === i ? 'bg-blue-500 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
        >
          {i}
        </button>
      );
    }

    // Next button
    buttons.push(
      <button
        key="next"
        onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
        disabled={currentPage === totalPages}
        className={`px-3 py-1 rounded ${currentPage === totalPages ? 'bg-gray-200 text-gray-500' : 'bg-gray-300 hover:bg-gray-400 text-gray-700'}`}
      >
        Next &raquo;
      </button>
    );

    return buttons;
  };

  // Format money values
  const formatMoney = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Load initial data
  useEffect(() => {
    fetchCampaigns();
  }, []);

  // Fetch data when tab, period, campaign or page changes
  useEffect(() => {
    if (selectedCampaign) {
      if (activeTab === 'dealer') {
        fetchDealerData(currentPage);
      }
    }
  }, [activeTab, period, currentPage, selectedCampaign]);

  // Update summary when period or campaign changes
  useEffect(() => {
    if (selectedCampaign) {
      fetchSummary();
    }
  }, [period, selectedCampaign]);

  // Reset selected dealer when campaign changes
  useEffect(() => {
    setSelectedDealerId(null);
    setDealerDetails(null);
  }, [selectedCampaign]);

  // Handler to redirect to campaign reports
  const handleRedirectToCampaignReports = () => {
    navigate('/admin/campaign-reports');
  };
  
  // Handler to dismiss the banner
  const handleDismissBanner = () => {
    setShowDeprecationBanner(false);
  };

  return (
    <div className="p-4">
      {showDeprecationBanner && (
        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-6 flex items-center justify-between">
          <div className="flex items-center">
            <FaExclamationTriangle className="mr-3 text-yellow-500" />
            <div>
              <p className="font-bold">Deprecation Notice</p>
              <p>This Schemes Reports module has been replaced by the new Campaign Reports module.</p>
              <p className="text-sm">The Campaign Reports provide enhanced features including company selection and improved stock group hierarchy-based calculations.</p>
            </div>
          </div>
          <div className="flex flex-col space-y-2">
            <button 
              onClick={handleRedirectToCampaignReports}
              className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded"
            >
              Go to Campaign Reports
            </button>
            <button 
              onClick={handleDismissBanner}
              className="bg-gray-300 hover:bg-gray-400 text-gray-800 py-1 px-3 rounded text-sm"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
      
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Schemes Reports (Legacy)</h1>
      </div>

      {/* Period and Campaign selection */}
      <div className="flex flex-wrap justify-between items-center bg-white p-4 rounded-lg shadow mb-6">
        {/* Period selector */}
        <div className="mb-2 sm:mb-0">
          <label className="mr-2 font-medium text-gray-700">Period:</label>
          <div className="inline-flex">
            <select
              value={period}
              onChange={(e) => handlePeriodChange(e.target.value)}
              className="rounded border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="this_week">This Week</option>
              <option value="last_week">Last Week</option>
              <option value="this_month">This Month</option>
              <option value="last_month">Last Month</option>
              <option value="this_quarter">This Quarter</option>
              <option value="last_quarter">Last Quarter</option>
              <option value="this_year">This Year</option>
              <option value="last_year">Last Year</option>
              <option value="all_time">All Time</option>
            </select>
          </div>
        </div>

        {/* Campaign selector */}
        <div>
          <label className="mr-2 font-medium text-gray-700">Campaign:</label>
          <div className="inline-flex">
            <select
              value={selectedCampaign}
              onChange={handleCampaignChange}
              className="rounded border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="">Select Campaign</option>
              {campaigns.map(campaign => (
                <option key={campaign._id} value={campaign._id}>
                  {campaign.name} {campaign.is_active && new Date(campaign.date_from) <= new Date() && new Date(campaign.date_to) >= new Date() ? '(Active)' : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Export button */}
        <div>
          <button
            onClick={handleExport}
            className="bg-green-500 hover:bg-green-600 text-white py-1 px-3 rounded flex items-center"
            disabled={loading || dealerData.length === 0}
          >
            <FaDownload className="mr-2" /> Export
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Total Dealers</h3>
          <p className="text-3xl font-bold text-blue-600">{summary.totalDealers || 0}</p>
          <p className="text-sm text-gray-500">Active: {summary.activeDealers || 0}</p>
        </div>

        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Total Vouchers</h3>
          <p className="text-3xl font-bold text-green-600">{summary.totalVouchers || 0}</p>
        </div>

        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Total Sales</h3>
          <p className="text-3xl font-bold text-purple-600">{formatMoney(summary.totalSales || 0)}</p>
        </div>

        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Total Points</h3>
          <p className="text-3xl font-bold text-orange-600">{summary.totalPoints || 0}</p>
        </div>
      </div>

      {/* Main content area */}
      <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
        <div className="flex flex-wrap space-x-1 px-4 py-2 bg-gray-50 border-b">
          <button
            className={`px-4 py-2 rounded-t-lg ${activeTab === 'dealer' ? 'bg-blue-500 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
            onClick={() => setActiveTab('dealer')}
          >
            Dealer Performance
          </button>
        </div>

        {/* Dealer Performance Tab */}
        {activeTab === 'dealer' && (
          <div>
            {loading ? (
              <div className="p-8 flex justify-center">
                <LoadingSpinner />
              </div>
            ) : dealerData.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No dealer data available for the selected period and campaign.
              </div>
            ) : (
              <div className="flex flex-col lg:flex-row">
                {/* Dealer list */}
                <div className={`${selectedDealerId ? 'lg:w-1/2' : 'w-full'} p-4`}>
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dealer</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sales</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Points</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vouchers</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {dealerData.map((dealer) => (
                        <tr
                          key={dealer.dealerId}
                          onClick={() => handleDealerClick(dealer.dealerId)}
                          className={`cursor-pointer hover:bg-gray-50 ${selectedDealerId === dealer.dealerId ? 'bg-blue-50' : ''}`}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{dealer.dealerName}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{formatMoney(dealer.totalSales)}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-blue-600">{dealer.totalPoints}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{dealer.voucherCount}</div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Pagination */}
                  <div className="flex justify-center mt-4 space-x-1">
                    {renderPaginationButtons()}
                  </div>
                </div>

                {/* Dealer details */}
                {selectedDealerId && (
                  <div className="lg:w-1/2 p-4 border-t lg:border-t-0 lg:border-l border-gray-200">
                    {loadingDetails ? (
                      <div className="p-8 flex justify-center">
                        <LoadingSpinner />
                      </div>
                    ) : dealerDetails ? (
                      <div>
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="text-lg font-semibold">Dealer Details</h3>
                          <button
                            onClick={handleExportDetails}
                            className="bg-green-500 hover:bg-green-600 text-white py-1 px-3 rounded flex items-center text-sm"
                          >
                            <FaDownload className="mr-1" /> Export Details
                          </button>
                        </div>

                        <div className="bg-gray-50 p-4 mb-4 rounded">
                          <h4 className="text-md font-medium">{dealerDetails.dealer.name}</h4>
                          {dealerDetails.dealer.address && <p className="text-sm text-gray-600">{dealerDetails.dealer.address}</p>}
                          {dealerDetails.dealer.email && <p className="text-sm text-gray-600">{dealerDetails.dealer.email}</p>}

                          <div className="grid grid-cols-3 gap-4 mt-3">
                            <div>
                              <p className="text-xs text-gray-500">Total Sales</p>
                              <p className="text-sm font-medium">{formatMoney(dealerDetails.summary.totalSales)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Total Points</p>
                              <p className="text-sm font-medium">{dealerDetails.summary.totalPoints}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Voucher Count</p>
                              <p className="text-sm font-medium">{dealerDetails.summary.voucherCount}</p>
                            </div>
                          </div>
                        </div>

                        <h4 className="font-medium mb-2">Vouchers</h4>

                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200 text-sm">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">No.</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Points</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {dealerDetails.vouchers.map((voucher, index) => (
                                <tr key={voucher.voucherId} className="hover:bg-gray-50">
                                  <td className="px-3 py-2 whitespace-nowrap">{voucher.voucherNumber}</td>
                                  <td className="px-3 py-2 whitespace-nowrap">
                                    {format(new Date(voucher.date), 'yyyy-MM-dd')}
                                  </td>
                                  <td className="px-3 py-2 whitespace-nowrap">{formatMoney(voucher.totalAmount)}</td>
                                  <td className="px-3 py-2 whitespace-nowrap">{voucher.totalPoints}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      <div className="p-4 text-center text-gray-500">
                        Select a dealer to view details
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
